import { useState, useEffect } from "react";

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

function setCookie(name, value, days = 365) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [personalization, setPersonalization] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const consent = getCookie("pord_cookie_consent");
    if (!consent) {
      const t = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  // Also listen for footer "Cookie" link click
  useEffect(() => {
    const handler = () => { setShow(true); setShowPrefs(true); };
    window.addEventListener("open-cookie-prefs", handler);
    return () => window.removeEventListener("open-cookie-prefs", handler);
  }, []);

  const accept = () => {
    setCookie("pord_cookie_consent", JSON.stringify({ essential: true, personalization: true, analytics: false }));
    setShow(false);
    setShowPrefs(false);
  };

  const savePrefs = () => {
    setCookie("pord_cookie_consent", JSON.stringify({ essential: true, personalization, analytics }));
    setShow(false);
    setShowPrefs(false);
  };

  if (!show) return null;

  return (
    <div className="cookie-banner" data-testid="cookie-banner">
      <div style={{ maxWidth: 672, margin: "0 auto" }}>
        {!showPrefs ? (
          /* Main banner */
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <p style={{ flex: 1, minWidth: 200, fontSize: "0.85rem", fontWeight: 300, color: "rgba(200,204,198,0.75)", lineHeight: 1.6, margin: 0 }}>
              We use cookies to keep this page running and improve your experience. Simply continue or click "Noted" to acknowledge our cookie policy.
            </p>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <button className="cta-pill" onClick={accept} data-testid="cookie-accept-btn" style={{ padding: "10px 24px" }}>
                Noted
              </button>
              <button className="ghost-link" onClick={() => setShowPrefs(true)} data-testid="cookie-prefs-btn" style={{ textDecoration: "underline" }}>
                Preferences
              </button>
            </div>
          </div>
        ) : (
          /* Preferences panel */
          <div>
            <div style={{ marginBottom: 20 }}>
              {/* Essential — always on */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid rgba(184,212,208,0.06)" }}>
                <div>
                  <p style={{ color: "rgba(220,224,218,0.85)", fontSize: "0.9rem", margin: 0, fontWeight: 400 }}>Essential</p>
                  <p style={{ color: "rgba(163,163,163,0.5)", fontSize: "0.78rem", margin: "2px 0 0" }}>Keeps the experience running. Cannot be disabled.</p>
                </div>
                <div style={{ background: "var(--accent-teal)", borderRadius: 999, width: 40, height: 22, display: "flex", alignItems: "center", justifyContent: "flex-end", padding: "0 3px", opacity: 0.6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff" }} />
                </div>
              </div>
              {/* Personalization */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid rgba(184,212,208,0.06)", cursor: "pointer" }} onClick={() => setPersonalization(!personalization)}>
                <div>
                  <p style={{ color: "rgba(220,224,218,0.85)", fontSize: "0.9rem", margin: 0, fontWeight: 400 }}>Personalization</p>
                  <p style={{ color: "rgba(163,163,163,0.5)", fontSize: "0.78rem", margin: "2px 0 0" }}>Remembers your progress and preferences.</p>
                </div>
                <div data-testid="toggle-personalization" style={{ background: personalization ? "var(--accent-teal)" : "rgba(163,163,163,0.2)", borderRadius: 999, width: 40, height: 22, display: "flex", alignItems: "center", justifyContent: personalization ? "flex-end" : "flex-start", padding: "0 3px", transition: "all 0.2s ease" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "all 0.2s ease" }} />
                </div>
              </div>
              {/* Analytics */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", cursor: "pointer" }} onClick={() => setAnalytics(!analytics)}>
                <div>
                  <p style={{ color: "rgba(220,224,218,0.85)", fontSize: "0.9rem", margin: 0, fontWeight: 400 }}>Analytics</p>
                  <p style={{ color: "rgba(163,163,163,0.5)", fontSize: "0.78rem", margin: "2px 0 0" }}>Helps us understand what's resonating. No ads.</p>
                </div>
                <div data-testid="toggle-analytics" style={{ background: analytics ? "var(--accent-teal)" : "rgba(163,163,163,0.2)", borderRadius: 999, width: 40, height: 22, display: "flex", alignItems: "center", justifyContent: analytics ? "flex-end" : "flex-start", padding: "0 3px", transition: "all 0.2s ease" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "all 0.2s ease" }} />
                </div>
              </div>
            </div>
            <button className="cta-pill" onClick={savePrefs} data-testid="cookie-save-prefs-btn" style={{ padding: "10px 24px" }}>
              Save Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Export a function to open prefs from footer
export function openCookiePrefs() {
  window.dispatchEvent(new Event("open-cookie-prefs"));
}
